/**
 * computeRerunDiff — pure helper that summarises the difference between
 * a parent report's persisted configuration and the current rerun-panel
 * edits, for the RP1 inline diff chip + popover.
 *
 * Three categories of change feed in:
 *   1. Document list — additions (uploadedDocs that aren't on the
 *      parent report) and removals (parent-report docs the user has
 *      excluded).
 *   2. Report-type preferences — typed-pref keys whose value differs
 *      from the baseline the panel was seeded with.
 *   3. Site project intent — the cross-cutting workload / nuclear /
 *      priority dropdowns the user can edit on the merged Configuration
 *      step.
 *
 * The chip renders a compact summary like `+1 doc · +2 prefs`; the
 * popover renders the full breakdown grouped by category. Both read
 * from the same `RerunDiffSummary` shape returned here.
 *
 * Pure presentation — no React, no store reads. Trivially testable.
 */
import type {
  ProjectIntent,
  SiteDocument,
  SiteRagReport,
  TypedPreferencesByType,
} from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';

export interface RerunDiffEntry {
  /** Human-readable label for the changed field. */
  label: string;
  /** Old value (omitted for additions like "+1 document"). */
  from?: string;
  /** New value. For document additions / removals this is the
   *  filename / "(removed)" sentinel. */
  to: string;
}

export interface RerunDiffBreakdown {
  /** Documents newly added to the rerun set. */
  newDocs: RerunDiffEntry[];
  /** Documents that were on the parent report but are now excluded. */
  removedDocs: RerunDiffEntry[];
  /** Per-report-type preference changes (typed-pref deltas). */
  prefChanges: RerunDiffEntry[];
  /** Cross-cutting site project intent changes. */
  intentChanges: RerunDiffEntry[];
}

export interface RerunDiffSummary {
  /** Sum of all entries across the breakdown — drives the chip
   *  visibility (`> 0` shows the chip, `=== 0` hides it). */
  totalChanges: number;
  /** Count per category — handy for the compact chip caption like
   *  `+N doc · +M prefs`. Mirror of the breakdown lengths. */
  counts: {
    newDocs: number;
    removedDocs: number;
    prefChanges: number;
    intentChanges: number;
  };
  /** Full per-category entry list — drives the popover content. */
  breakdown: RerunDiffBreakdown;
}

/** Stable label map for typed-pref keys — mirrors the existing
 *  TYPED_PREF_LABELS table inside FeasibilityRerunPanel so the chip
 *  popover reads the same captions as the review-step diff. */
const TYPED_PREF_LABELS: Record<string, string> = {
  projectSizeMW: 'Project size (MW)',
  powerRequirementMW: 'Power requirement (MW)',
  coolingPreference: 'Cooling preference',
  coolingType: 'Cooling type',
  inServiceDate: 'In-service date',
  timeToPowerCriticalDate: 'Time-to-power date',
  customerCategory: 'Customer category',
  riskTolerance: 'Risk tolerance',
  powerSourcePreference: 'Power source',
  btmStrategy: 'BTM strategy',
  renewablesPreference: 'Renewables',
  curtailmentRiskTolerance: 'Curtailment risk',
  expectedNepaPathway: 'NEPA pathway',
  airBasinOverride: 'Air basin',
  dischargeWaterTarget: 'Discharge target',
  federalNexus: 'Federal nexus',
  tribalLandProximityKnown: 'Tribal land proximity',
  droughtScenario: 'Drought scenario',
  wastewaterTarget: 'Wastewater target',
  waterRightsKnown: 'Water rights',
  targetFootprintAcres: 'Target footprint (ac)',
  phasing: 'Phasing',
  phaseCount: 'Phase count',
  setbackOverrideFt: 'Setback (ft)',
  parkingRatioPerEmployee: 'Parking ratio',
  stormwaterDetentionGal: 'Stormwater detention (gal)',
  carrierDiversityMin: 'Carrier diversity (min)',
  bandwidthGbps: 'Bandwidth (Gbps)',
  ixPeeringRequired: 'IX peering required',
  submarineCableProximityImportant: 'Submarine-cable proximity',
  kpiWeightOverrides: 'KPI weight overrides',
  speciesConcerns: 'Species concerns',
  latencyTargetsMs: 'Latency targets',
};

function labelForTypedPref(key: string): string {
  return TYPED_PREF_LABELS[key] ?? key;
}

function formatTypedPrefValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function intentEntries(
  parent: ProjectIntent | undefined,
  edited: ProjectIntent,
): RerunDiffEntry[] {
  const out: RerunDiffEntry[] = [];
  if (!parent) return out;
  if (parent.workloadProfile !== edited.workloadProfile) {
    out.push({
      label: 'Workload profile',
      from: String(parent.workloadProfile ?? '—'),
      to: String(edited.workloadProfile ?? '—'),
    });
  }
  if (parent.nuclearInterest !== edited.nuclearInterest) {
    out.push({
      label: 'Nuclear interest',
      from: String(parent.nuclearInterest ?? '—'),
      to: String(edited.nuclearInterest ?? '—'),
    });
  }
  if (parent.priority !== edited.priority) {
    out.push({
      label: 'Priority',
      from: String(parent.priority ?? '—'),
      to: String(edited.priority ?? '—'),
    });
  }
  return out;
}

function prefEntries(
  baseline: Record<string, unknown> | undefined,
  edited: Record<string, unknown> | undefined,
): RerunDiffEntry[] {
  const a = baseline ?? {};
  const b = edited ?? {};
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const out: RerunDiffEntry[] = [];
  for (const key of keys) {
    const av = a[key];
    const bv = b[key];
    const same = typeof av === 'object' || typeof bv === 'object'
      ? JSON.stringify(av ?? null) === JSON.stringify(bv ?? null)
      : av === bv;
    if (same) continue;
    out.push({
      label: labelForTypedPref(key),
      from: formatTypedPrefValue(av),
      to: formatTypedPrefValue(bv),
    });
  }
  return out;
}

export interface ComputeRerunDiffArgs {
  report: SiteRagReport;
  /** Currently-edited document set on the rerun panel (uploads +
   *  library picks + the original report docs minus user removals). */
  editedDocs: SiteDocument[];
  /** Document ids the user has un-checked on the rerun panel — these
   *  are documents that WERE on the parent report but won't ride along
   *  for the rerun. */
  excludedDocIds: ReadonlySet<string>;
  /** Currently-edited typed prefs for the parent report's type. */
  editedPrefs: TypedPreferencesByType[ReportTypeId] | undefined;
  /** Baseline typed prefs the rerun panel was seeded with (i.e. what
   *  was persisted at mount time — see `deriveInitialRerunPrefs`). */
  baselinePrefs: TypedPreferencesByType[ReportTypeId] | undefined;
  /** Currently-edited project intent on the rerun panel. */
  projectIntent: ProjectIntent;
}

/**
 * Compute the full RP1 diff summary from the rerun panel's current
 * editable state vs the parent report's persisted state.
 *
 * The chip uses `.totalChanges` to decide whether to render and
 * `.counts` to format its compact caption. The popover walks
 * `.breakdown` to render the per-category groups.
 */
export function computeRerunDiff(args: ComputeRerunDiffArgs): RerunDiffSummary {
  const { report, editedDocs, excludedDocIds, editedPrefs, baselinePrefs, projectIntent } = args;

  // 1. Documents. New docs = anything on editedDocs that isn't on the
  //    parent report's documents list. Removed docs = anything on the
  //    parent report that's in excludedDocIds.
  const parentDocs = report.documents ?? [];
  const parentDocIds = new Set(parentDocs.map(d => d.id));
  const newDocs: RerunDiffEntry[] = editedDocs
    .filter(d => !parentDocIds.has(d.id))
    .map(d => ({ label: 'Document', to: d.fileName }));
  const removedDocs: RerunDiffEntry[] = parentDocs
    .filter(d => excludedDocIds.has(d.id))
    .map(d => ({ label: 'Document', from: d.fileName, to: '(removed)' }));

  // 2. Typed preferences. Diffed against the panel's baseline so an
  //    edit-then-revert collapses to zero (same logic as the review
  //    step diff and the auto-version-note in FeasibilityRerunPanel).
  const prefChanges = prefEntries(
    baselinePrefs as Record<string, unknown> | undefined,
    editedPrefs as Record<string, unknown> | undefined,
  );

  // 3. Site project intent. The parent's intent (workload / nuclear /
  //    priority — the three site-level cross-cutting fields) lives on
  //    `report.rerunConfig.projectIntent` for reports generated post-W*
  //    and falls back to undefined for very-legacy fixtures.
  const intentChanges = intentEntries(report.rerunConfig?.projectIntent, projectIntent);

  const counts = {
    newDocs: newDocs.length,
    removedDocs: removedDocs.length,
    prefChanges: prefChanges.length,
    intentChanges: intentChanges.length,
  };
  const totalChanges =
    counts.newDocs + counts.removedDocs + counts.prefChanges + counts.intentChanges;

  return {
    totalChanges,
    counts,
    breakdown: { newDocs, removedDocs, prefChanges, intentChanges },
  };
}

/**
 * Format a one-line caption for the chip. Skips zero-count categories
 * and abbreviates pluralisation aggressively to fit on a single button.
 * Example: `+1 doc · +2 prefs`.
 */
export function formatRerunDiffChipCaption(summary: RerunDiffSummary): string {
  const parts: string[] = [];
  const { counts } = summary;
  if (counts.newDocs > 0) parts.push(`+${counts.newDocs} doc${counts.newDocs === 1 ? '' : 's'}`);
  if (counts.removedDocs > 0) parts.push(`−${counts.removedDocs} doc${counts.removedDocs === 1 ? '' : 's'}`);
  if (counts.prefChanges > 0) parts.push(`+${counts.prefChanges} pref${counts.prefChanges === 1 ? '' : 's'}`);
  if (counts.intentChanges > 0) parts.push(`+${counts.intentChanges} intent`);
  return parts.join(' · ');
}
